import React, { useState } from "react";
import { motion } from "framer-motion";
import contact1 from "../../Assets/Images/contact1.png";
import { useTranslation } from "react-i18next";
import emailIcon from "../../Assets/Icons/Mail.svg";
import telephone from "../../Assets/Icons/telephone.svg";
import workLocation from "../../Assets/Icons/workLocation.svg";
import date from "../../Assets/Icons/building.svg";
import * as Yup from "yup";
import { useFormik } from "formik";
import FieldInput from "../../Components/FieldInput";
import Button from "../../Components/Button";
import API from "../../Common/API";
import { useDispatch } from "react-redux";
import { setAlert } from "../../Store/Theme/actions";

const initialContactForm = {
  name: "",
  email: "",
  mobile: "",
  message: "",
};

const ContactPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [submitDisable, setSubmitDisable] = useState(false);

  const contactFormSchema = Yup.object().shape({
    name: Yup.string().required("Full Name is required"),
    email: Yup.string().nullable(),
    mobile: Yup.string().required("Mobile is required"),
    message: Yup.string().required("Message is required"),
  });

  const contactForm = useFormik({
    initialValues: initialContactForm,
    validateOnMount: true,
    validationSchema: contactFormSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      setSubmitDisable(true);
      API.post("/common/insertEnquiry", values)
        .then((apiRes) => {
          if (apiRes.status === 200) {
            dispatch(
              setAlert({
                title: "Success",
                subtitle: t(
                  "Enquiry submitted successfully. Will contact you shortly."
                ),
                type: "success",
                active: true,
              })
            );
            contactForm.resetForm();
            setSubmitDisable(false);
          }
        })
        .catch((err) => {
          dispatch(
            setAlert({
              title: "Error",
              subtitle: t("Something went wrong. Please try again later."),
              type: "error",
              active: true,
            })
          );
          setSubmitDisable(false);
        });
    },
  });

  return (
    <motion.div className="w-full min-h-screen flex flex-col relative">
      {/* Header Section with Image */}
      <motion.div className="relative w-full">
        <img
          src={contact1}
          className="w-full h-[300px] sm:h-[400px] md:h-[500px] object-cover"
          alt="contact"
        />
        <motion.div className="absolute inset-0 flex items-center justify-center px-4 md:px-20">
          <div className="bg-white/80 rounded-lg p-4 sm:p-6 md:p-10 lg:p-12 w-full md:w-2/3 lg:w-1/2 text-center md:text-left">
            <p className="text-2xl md:text-3xl font-bold text-primeColor mb-3">
              {t(`Contact Us`)}
            </p>
            <p className="text-base md:text-lg text-black font-semibold leading-relaxed">
              {t(
                `We'd love to hear from you! Whether you have questions, need assistance, or want to share your experiences, our team is here to help. Please reach out to us through any of the methods below, and we'll get back to you as soon as possible.`
              )}
            </p>
          </div>
        </motion.div>
      </motion.div>

      {/* Content Section */}
      <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8 px-4 md:px-10 lg:px-20 py-10">
        {/* Contact Info */}
        <motion.div className="flex flex-col gap-6">
          {/* Email */}
          <div className="flex items-center gap-3">
            <img src={emailIcon} className="w-6 h-6" alt="email" />
            <span className="text-primeColor font-bold">{t("Email")}:</span>
            <a
              href="mailto:royalkaverycs@gmail.com"
              className="text-black underline font-semibold"
            >
              royalkaverycs@gmail.com
            </a>
          </div>

          {/* Phone */}
          <div className="flex items-center gap-3">
            <img src={telephone} className="w-6 h-6" alt="phone" />
            <span className="text-primeColor font-bold">{t("Phone")}:</span>
            <a
              href="tel:+918248369800"
              className="text-black underline font-semibold"
            >
              +91 82483 69800
            </a>
          </div>

          {/* Office Hours */}
          <div>
            <div className="flex items-center gap-3">
              <img src={date} className="w-6 h-6" alt="hours" />
              <span className="text-primeColor font-bold">{t("Office Hours")}</span>
            </div>
            <p className="pl-8 font-semibold text-[16px]">
              {t("Monday - Saturday")} : 10:00 AM - 05:00 PM
            </p>
            <p className="pl-8 font-semibold text-[16px]">
              {t("Sunday")} : {t("Holiday")}
            </p>
          </div>

          {/* Office Address */}
          <div>
            <div className="flex items-center gap-3">
              <img src={workLocation} className="w-6 h-6" alt="location" />
              <span className="text-primeColor font-bold">{t("Office Address")}</span>
            </div>
            <p className="pl-8 font-semibold text-[16px]">A.P Complex, Gandhiji Nagar,</p>
            <p className="pl-8 font-semibold text-[16px]">Near Kubera Vinayagar Kovil,</p>
            <p className="pl-8 font-semibold text-[16px]">E.B Colony, N.K Road,</p>
            <p className="pl-8 font-semibold text-[16px]">Thanjavur - 613006</p>
          </div>
        </motion.div>

        {/* Divider */}
        <motion.div className="hidden md:flex items-center justify-center">
          <div className="h-full w-0.5 bg-primeColor" />
        </motion.div>

        {/* Contact Form */}
        <motion.div className="flex flex-col w-full">
          <p className="text-xl md:text-2xl font-bold text-primeColor text-center md:text-left">
            {t("Contact Form")}
          </p>
          <div className="flex flex-col gap-5 mt-8">
            <FieldInput
              placeholder={t("Full Name")}
              fieldProps={contactForm.getFieldProps("name")}
              fieldHelper={contactForm.getFieldHelpers("name")}
              fieldMeta={contactForm.getFieldMeta("name")}
              edit={true}
              dataLoaded={true}
            />
            <FieldInput
              placeholder={t("Email")}
              fieldProps={contactForm.getFieldProps("email")}
              fieldHelper={contactForm.getFieldHelpers("email")}
              fieldMeta={contactForm.getFieldMeta("email")}
              edit={true}
              dataLoaded={true}
            />
            <FieldInput
              placeholder={t("Mobile Number")}
              fieldProps={contactForm.getFieldProps("mobile")}
              fieldHelper={contactForm.getFieldHelpers("mobile")}
              fieldMeta={contactForm.getFieldMeta("mobile")}
              edit={true}
              dataLoaded={true}
            />
            <FieldInput
              type="textarea"
              placeholder={t("Message")}
              fieldProps={contactForm.getFieldProps("message")}
              fieldHelper={contactForm.getFieldHelpers("message")}
              fieldMeta={contactForm.getFieldMeta("message")}
              edit={true}
              dataLoaded={true}
            />
            <Button
              type="primary"
              disabled={submitDisable}
              onClick={() => !submitDisable && contactForm.submitForm()}
            >
              {t("Submit")}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default ContactPage;